import React, { useCallback, useMemo } from 'react';
import * as R from 'ramda';
import useFetchUserBranchDetails from 'App/hooks/useFetchUserBranchDetails';
import { camelCase, keys, pickBy, startCase } from 'lodash';
import { useUserContext } from 'App/contexts/UserContext';
import useSetDefaultBranch from 'App/hooks/useSetDefaultBranch';
import useChangeBranch from 'App/hooks/useChangeBranch';
import toast from 'Shared/toast';
import BranchNameCell from './BranchNameCell';
import ActionCell from './ActionCell';
import ActionHeader from './ActionHeader';
import Table from 'App/components/Table';
import { USER_PRIVILEGE_OPTIONS } from '../UserManager/constants';

const privilegeMapping = USER_PRIVILEGE_OPTIONS.reduce(
    (acc, cur) => {
        acc[cur.value] = cur.label;
        return acc;
    },
    {
        companyAdmin: 'Company Admin',
    }
);

const tableStateReducer = (state, action, prevState) => {
    const initialSort = [{ id: 'name', desc: false }];

    const { type } = action;
    switch (type) {
        case 'gotoPage':
        case 'toggleSortBy':
        case 'setPageSize':
            return state;
        case 'setFilter':
            return { ...state, pageIndex: 0 };
        case 'init': {
            return R.set(R.lensProp('sortBy'), initialSort, state);
        }
        default:
            return R.isEmpty(prevState) ? state : prevState;
    }
};

const AccessViewTable = () => {
    const { isLoading, data: branches } = useFetchUserBranchDetails();
    const user = useUserContext();
    const { handleChangeBranch } = useChangeBranch();
    const { BranchID: branchId } = user;
    const useSetDefaultBranchMutation = useSetDefaultBranch();

    const setDefaultBranch = useCallback(
        (val) => {
            useSetDefaultBranchMutation.mutate(val);
            handleChangeBranch(val);
        },
        [handleChangeBranch, useSetDefaultBranchMutation]
    );

    const setActiveBranch = useCallback(
        (val) => {
            handleChangeBranch(val)
                .then(() => toast.success('Branch has been changed successfully.'))
                .catch(() => toast.error('Failed to change branch.'));
        },
        [handleChangeBranch]
    );

    const columns = useMemo(
        () => [
            {
                Header: 'Branch Name',
                accessor: 'Name',
                id: 'name',
                Cell: BranchNameCell,
                style: { width: '20%' },
            },
            {
                Header: 'Privileges',
                id: 'privileges',
                accessor: (row) =>
                    keys(pickBy(row.Privileges, (v) => v === true))
                        .map((i) => privilegeMapping[camelCase(i)] ?? startCase(i))
                        .join(', '),
                style: { width: '65%' },
                disableSort: true,
                canFilter: false,
                Filter: '',
            },
            {
                Header: ActionHeader,
                accessor: 'actions',
                id: 'actions',
                disableSort: true,
                canFilter: false,
                Cell: ActionCell(setActiveBranch, setDefaultBranch),
                Filter: '',
            },
        ],
        [setActiveBranch, setDefaultBranch]
    );

    const tableData = useMemo(() => {
        return Object.values(branches || {}).map((b) => ({
            ...b,
            isActiveBranch: branchId === b.ID,
        }));
    }, [branches, branchId]);

    return (
        <Table
            columns={columns}
            data={tableData}
            placeholderText="No available branches"
            initialState={{
                sortBy: [
                    {
                        id: 'name',
                        desc: false,
                    },
                ],
                pageSize: 10,
            }}
            pageSizeOptions={[10, 25, 50]}
            isLoading={isLoading}
            getRowId={R.prop('id')}
            stateReducer={tableStateReducer}
        />
    );
};

export default AccessViewTable;
